/**
 * Framework-agnostic scheduled-task state machine.
 *
 * The pure scheduling engine: schedule definitions -> next-fire computation ->
 * ready-task detection -> post-fire state advance, with on-disk persistence so
 * nextFireAt/lastFireAt survive manager restarts. It owns the WHEN, not the HOW:
 * each framework supplies its own firing leaf and calls back into
 * `markTaskFired`.
 *   - Claude Code fires via the in-session kanban todo-card route
 *     (manager/scheduler/execution.ts + kanban-route.ts).
 *   - opencode fires via an HTTP inject into `opencode serve`
 *     (manager/opencode-scheduler.ts).
 * Both share this engine verbatim, so cadence, one-shot completion,
 * manual-trigger, and cross-restart recovery are identical across frameworks.
 * (Named claude-scheduler.ts historically, when the claude `-p` oneshot was the
 * only consumer.)
 */

import { existsSync, mkdirSync, readFileSync, renameSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';
import { homedir } from 'node:os';
import { Cron } from 'croner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SchedulerTaskState {
  taskId: string;
  templateId: string;
  name: string;
  agentCodeName: string;
  agentId: string;
  scheduleKind: 'cron' | 'every' | 'at';
  scheduleExpr: string | null;
  scheduleEvery: string | null;
  scheduleAt: string | null;
  timezone: string;
  prompt: string;
  sessionTarget: string;
  deliveryMode: string;
  /** ENG-6107: always | conditional | never — whether results deliver.
   *  Optional: states persisted by older CLIs lack it; absent = 'always'. */
  deliveryPolicy?: string | null;
  deliveryChannel: string | null;
  /** ENG-4422 §4: JSONB DeliveryTarget (or null). Legacy string form is gone
   *  after the migration; in-memory shape is the structured object. */
  deliveryTo: unknown | null;
  enabled: boolean;
  triggeredAt: number | null; // epoch ms — manual trigger from webapp
  /** ENG-8154: the `triggeredAt` value this task has already been fired for.
   *  A manual trigger is a ONE-SHOT COMMAND, but `scheduled_tasks.triggered_at`
   *  is only ever set server-side and never cleared, so the manager re-reads the
   *  same historical trigger on every sync, forever. Consumption used to be
   *  inferred from `lastFireAt`, which is null on any cold entry into state —
   *  so a task that had ever been triggered re-fired immediately, ignoring its
   *  own nextFireAt. Tracking the consumed trigger explicitly makes the trigger
   *  idempotent per occurrence and independent of fire history.
   *  Optional: states persisted by older CLIs lack it; normalised on load. */
  consumedTriggeredAt?: number | null;
  nextFireAt: number | null; // epoch ms, null = completed one-shot
  lastFireAt: number | null;
  lastStatus: 'ok' | 'error' | null;
  firedCount: number;
}

export interface SchedulerState {
  version: 1;
  tasks: Record<string, SchedulerTaskState>;
  updatedAt: string;
}

export interface SchedulerTaskInput {
  id: string;
  template_id: string;
  name: string;
  schedule_kind: 'cron' | 'every' | 'at';
  schedule_expr: string | null;
  schedule_every: string | null;
  schedule_at: string | null;
  timezone: string;
  prompt: string;
  session_target: string;
  delivery_mode: string;
  /** ENG-6107: absent from older APIs — treated as 'always'. */
  delivery_policy?: string | null;
  delivery_channel: string | null;
  /** ENG-4422 §4: JSONB DeliveryTarget or null (post-migration). */
  delivery_to: unknown | null;
  enabled: boolean;
  triggered_at?: string | null;
}

// ---------------------------------------------------------------------------
// Interval parsing (reused from Claude Code adapter)
// ---------------------------------------------------------------------------

function parseIntervalMs(scheduleEvery: string | null): number {
  if (!scheduleEvery) return 60 * 60_000; // 1hr default
  const match = scheduleEvery.match(/^(\d+)\s*(m|min|h|hr|d)$/i);
  if (!match) return 60 * 60_000;
  const value = parseInt(match[1]!, 10);
  const unit = match[2]!.toLowerCase();
  if (unit === 'h' || unit === 'hr') return value * 60 * 60_000;
  if (unit === 'd') return value * 24 * 60 * 60_000;
  return value * 60_000; // minutes
}

// ---------------------------------------------------------------------------
// Next-fire computation
// ---------------------------------------------------------------------------

export function computeNextFire(
  kind: 'cron' | 'every' | 'at',
  expr: string | null,
  every: string | null,
  at: string | null,
  timezone: string,
  afterMs?: number,
): number | null {
  const now = afterMs ?? Date.now();

  if (kind === 'cron' && expr) {
    try {
      const cron = new Cron(expr, { timezone: timezone || undefined });
      const next = cron.nextRun(new Date(now));
      return next ? next.getTime() : null;
    } catch {
      return null;
    }
  }

  if (kind === 'every') {
    const intervalMs = parseIntervalMs(every);
    return now + intervalMs;
  }

  if (kind === 'at' && at) {
    const ts = new Date(at).getTime();
    if (isNaN(ts)) return null;
    // If the 'at' timestamp is in the past and afterMs is set (meaning we already
    // fired), return null to prevent re-firing on state rebuild.
    if (afterMs && ts <= afterMs) return null;
    return ts;
  }

  return null;
}

// ---------------------------------------------------------------------------
// State persistence
// ---------------------------------------------------------------------------

/**
 * ENG-8154: how recent a `triggered_at` must be to count as a LIVE manual
 * trigger when a task enters scheduler state cold.
 *
 * `scheduled_tasks.triggered_at` is never cleared server-side, so on a cold
 * entry we cannot tell "the user just hit Run now" from "the user hit Run now
 * three days ago" by presence alone — and treating every cold-seen trigger as
 * live is precisely the defect (a task re-added by a sync fired on the spot,
 * ignoring its own nextFireAt). Age is the only signal available host-side.
 *
 * Sized against the manager's task-sync cadence, ~3 min in the field (the
 * ENG-8154 report shows polls at 06:08 / 06:11 / 06:14 / 06:17), so this allows
 * three missed polls before a genuine trigger is written off. Well under the
 * respawn cadence that produced the stale-trigger storm, and far under the
 * multi-day age of the trigger in the incident.
 *
 * Known trade-off, deliberate: a "Run now" issued while the agent is down for
 * longer than this window will not fire on recovery. Firing a much older
 * trigger on an unrelated restart is the worse failure, and it is the one that
 * actually happened. The durable fix is to acknowledge and clear the trigger
 * server-side, which removes the need to guess from age at all.
 */
const TRIGGER_FRESHNESS_MS = 10 * 60_000;

/**
 * ENG-8154: is a trigger recent enough to still be owed a run on cold entry?
 * Returns false for null so callers can seed the consumed watermark safely.
 */
function isTriggerFresh(triggeredAtMs: number | null, nowMs = Date.now()): boolean {
  if (triggeredAtMs === null) return false;
  return nowMs - triggeredAtMs <= TRIGGER_FRESHNESS_MS;
}

function getStateDir(codeName: string): string {
  // ENG-4418: unified agent dir — scheduler state lives at the agent root
  // alongside other runtime state files (plugins.json, schedules.json,
  // registration.json). Legacy ~/.augmented/<codeName>/claudecode/scheduler-state.json
  // is migrated up by claudeCodeAdapter.getAgentDir on first poll.
  return join(homedir(), '.augmented', codeName);
}

function getStatePath(codeName: string): string {
  return join(getStateDir(codeName), 'scheduler-state.json');
}

export function loadSchedulerState(codeName: string): SchedulerState {
  const path = getStatePath(codeName);
  if (existsSync(path)) {
    try {
      const parsed: SchedulerState = JSON.parse(readFileSync(path, 'utf-8'));
      // ENG-8154 upgrade normalisation. A state file written by an older CLI has
      // no `consumedTriggeredAt`. Seeding it from the CURRENT `triggeredAt`
      // treats any trigger already on the task as consumed, so upgrading does not
      // itself fire a historical trigger — which is the exact bug being fixed.
      // A genuinely new manual trigger arrives with a LATER timestamp and still
      // fires normally.
      for (const task of Object.values(parsed.tasks ?? {})) {
        if (task.consumedTriggeredAt === undefined) {
          // Same freshness rule as the cold-add path: a trigger still inside the
          // window was plausibly issued moments ago and is left owed, anything
          // older is written off so upgrading cannot fire history.
          task.consumedTriggeredAt = isTriggerFresh(task.triggeredAt) ? null : (task.triggeredAt ?? null);
        }
      }
      return parsed;
    } catch { /* corrupted — start fresh */ }
  }
  return { version: 1, tasks: {}, updatedAt: new Date().toISOString() };
}

export function saveSchedulerState(codeName: string, state: SchedulerState): void {
  const dir = getStateDir(codeName);
  mkdirSync(dir, { recursive: true });
  state.updatedAt = new Date().toISOString();
  const path = getStatePath(codeName);
  const tmpPath = path + '.tmp';
  writeFileSync(tmpPath, JSON.stringify(state, null, 2));
  renameSync(tmpPath, path);
}

// ---------------------------------------------------------------------------
// Sync API tasks → scheduler state
// ---------------------------------------------------------------------------

export function syncTasksToScheduler(
  codeName: string,
  agentId: string,
  tasks: SchedulerTaskInput[],
): SchedulerState {
  const state = loadSchedulerState(codeName);
  const desiredIds = new Set(tasks.map((t) => t.id));

  // Remove tasks no longer in API
  for (const id of Object.keys(state.tasks)) {
    if (!desiredIds.has(id)) {
      delete state.tasks[id];
    }
  }

  // Add or update tasks
  for (const t of tasks) {
    const existing = state.tasks[t.id];
    if (existing) {
      // Only recompute nextFireAt if the schedule definition actually changed.
      // Without this guard, every sync cycle resets nextFireAt from "now",
      // preventing past-due tasks from ever being detected as ready.
      const scheduleChanged =
        existing.scheduleKind !== t.schedule_kind ||
        existing.scheduleExpr !== t.schedule_expr ||
        existing.scheduleEvery !== t.schedule_every ||
        existing.scheduleAt !== t.schedule_at ||
        existing.timezone !== t.timezone;

      // Update mutable fields, preserve fire history
      existing.name = t.name;
      existing.templateId = t.template_id;
      existing.scheduleKind = t.schedule_kind;
      existing.scheduleExpr = t.schedule_expr;
      existing.scheduleEvery = t.schedule_every;
      existing.scheduleAt = t.schedule_at;
      existing.timezone = t.timezone;
      existing.prompt = t.prompt;
      existing.sessionTarget = t.session_target;
      existing.deliveryMode = t.delivery_mode;
      existing.deliveryPolicy = t.delivery_policy ?? 'always';
      existing.deliveryChannel = t.delivery_channel;
      existing.deliveryTo = t.delivery_to;
      existing.enabled = t.enabled;
      if (t.triggered_at) existing.triggeredAt = new Date(t.triggered_at).getTime();
      if (scheduleChanged) {
        existing.nextFireAt = computeNextFire(
          t.schedule_kind, t.schedule_expr, t.schedule_every, t.schedule_at,
          t.timezone, existing.lastFireAt ?? undefined,
        );
      }
    } else {
      // New task
      state.tasks[t.id] = {
        taskId: t.id,
        templateId: t.template_id,
        name: t.name,
        agentCodeName: codeName,
        agentId,
        scheduleKind: t.schedule_kind,
        scheduleExpr: t.schedule_expr,
        scheduleEvery: t.schedule_every,
        scheduleAt: t.schedule_at,
        timezone: t.timezone,
        prompt: t.prompt,
        sessionTarget: t.session_target,
        deliveryMode: t.delivery_mode,
        deliveryPolicy: t.delivery_policy ?? 'always',
        deliveryChannel: t.delivery_channel,
        deliveryTo: t.delivery_to,
        enabled: t.enabled,
        triggeredAt: t.triggered_at ? new Date(t.triggered_at).getTime() : null,
        // ENG-8154: a task entering state COLD must not fire a trigger that was
        // already sitting on the row, so a STALE trigger is seeded as consumed.
        // This is the load-bearing line: syncTasksToScheduler deletes any task
        // absent from a sync's task list, so a transient partial list re-adds it
        // here — and before this, the fresh `lastFireAt: null` made `!lastFireAt`
        // true and it fired on the spot, whatever nextFireAt said. That is the
        // ~11-fires-in-75-min engine.
        //
        // A FRESH trigger is left unconsumed so it still fires. That case is
        // real: a task created and immediately "Run now"-ed enters state through
        // this branch carrying a live trigger, and seeding it unconditionally
        // would silently swallow the user's explicit request.
        consumedTriggeredAt: isTriggerFresh(t.triggered_at ? new Date(t.triggered_at).getTime() : null)
          ? null
          : (t.triggered_at ? new Date(t.triggered_at).getTime() : null),
        nextFireAt: computeNextFire(
          t.schedule_kind, t.schedule_expr, t.schedule_every, t.schedule_at, t.timezone,
        ),
        lastFireAt: null,
        lastStatus: null,
        firedCount: 0,
      };
    }
  }

  saveSchedulerState(codeName, state);
  return state;
}

// ---------------------------------------------------------------------------
// Ready-task detection
// ---------------------------------------------------------------------------

// ENG-4675: pass `inFlightTaskIds` so the manager's poll loop doesn't
// re-detect tasks that already have a `claude -p` subprocess running.
// Without this, every supervisor tick (~17s) saw the manual trigger as
// still ready (because lastFireAt is only set on completion, not on
// fire start), spamming "N ready task(s)" + "Firing task" log lines that
// looked like duplicate fires. The in-flight guard at the spawn site
// prevented actual duplicate subprocesses, but the log was misleading.
export function getReadyTasks(
  state: SchedulerState,
  inFlightTaskIds?: ReadonlySet<string>,
): SchedulerTaskState[] {
  const now = Date.now();
  const ready = Object.values(state.tasks).filter((t) => {
    if (!t.enabled) return false;
    if (inFlightTaskIds?.has(t.taskId)) return false;
    // Manual trigger: triggered_at is set and this exact trigger hasn't been
    // consumed yet. ENG-8154: consumption is tracked explicitly rather than
    // inferred from `lastFireAt`. The old test (`!t.lastFireAt || ...`) treated
    // "we have no fire history" as "this trigger is pending", so any cold entry
    // into scheduler state re-fired a long-dead trigger and — because this
    // branch returns before the nextFireAt check below — did so regardless of
    // when the task was actually next due.
    if (t.triggeredAt && t.triggeredAt > (t.consumedTriggeredAt ?? 0)) return true;
    // Normal schedule
    return t.nextFireAt !== null && t.nextFireAt <= now;
  });
  // Deduplicate by templateId — only fire one task per template per cycle
  const seen = new Set<string>();
  return ready.filter((t) => {
    if (seen.has(t.templateId)) return false;
    seen.add(t.templateId);
    return true;
  });
}

// ---------------------------------------------------------------------------
// Post-execution state update
// ---------------------------------------------------------------------------

export function markTaskFired(
  codeName: string,
  taskId: string,
  status: 'ok' | 'error',
): SchedulerState {
  const state = loadSchedulerState(codeName);
  const task = state.tasks[taskId];
  if (!task) return state;

  task.lastFireAt = Date.now();
  task.lastStatus = status;
  task.firedCount++;
  // ENG-8154: whatever caused this fire, the trigger currently on the task is
  // now spent. Recording it here (rather than relying on lastFireAt ordering)
  // keeps the trigger one-shot even if the fire-history watermark is later lost.
  if (task.triggeredAt) task.consumedTriggeredAt = task.triggeredAt;

  // Compute next fire
  if (task.scheduleKind === 'at') {
    // One-shot — mark as completed
    task.nextFireAt = null;
  } else {
    task.nextFireAt = computeNextFire(
      task.scheduleKind, task.scheduleExpr, task.scheduleEvery, task.scheduleAt,
      task.timezone, task.lastFireAt,
    );
  }

  // Persisting is best-effort: the SchedulerState the caller stores in memory
  // (claudeSchedulerStates) is the running source of truth — getReadyTasks
  // reads it, not the disk file. A disk-write failure (ENOSPC/EACCES) must NOT
  // throw, or the caller's in-memory state would never advance and the task
  // would be treated as ready again next poll (ENG-5599: a duplicate
  // scheduled-task card materialised every tick). The mutated `state` is
  // returned regardless; cross-restart recovery is a separate concern.
  try {
    saveSchedulerState(codeName, state);
  } catch (err) {
    console.error(
      `[scheduler-engine] markTaskFired: failed to persist state for '${codeName}': ${(err as Error).message}`,
    );
  }
  return state;
}

// ---------------------------------------------------------------------------
// Find a task by template ID (for work triggers)
// ---------------------------------------------------------------------------

export function findTaskByTemplate(state: SchedulerState, templateId: string): SchedulerTaskState | undefined {
  return Object.values(state.tasks).find(
    (t) => t.templateId === templateId && t.enabled,
  );
}

export function getProjectDir(codeName: string): string {
  return join(homedir(), '.augmented', codeName, 'project');
}
